import React, { useCallback } from 'react';
import { useCandidateAuth, CandidateAuthProvider } from './useCandidateAuth';
import { useAdminAuth, AdminAuthProvider } from './useAdminAuth';
import type { 
  AuthContextType
} from '../types/auth';

// Combined Auth Provider that wraps both candidate and admin providers
interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <AdminAuthProvider>
      <CandidateAuthProvider>
        {children}
      </CandidateAuthProvider>
    </AdminAuthProvider>
  );
}

/**
 * Compatibility bridge hook that detects user type and delegates to appropriate auth context
 * This maintains backward compatibility while using separate auth contexts internally
 */
export function useAuth(): AuthContextType {
  const candidateAuth = useCandidateAuth();
  const adminAuth = useAdminAuth();

  // Try to determine which auth to use based on current authentication state
  // Priority: Admin auth if authenticated, then candidate auth, then check URL path
  const currentPath = window.location.pathname;
  const isAdminRoute = currentPath.startsWith('/admin') || currentPath.startsWith('/auth/hr-login');

  // If admin is authenticated, use admin auth
  if (adminAuth.isAuthenticated) {
    return {
      isAuthenticated: adminAuth.isAuthenticated,
      isLoading: adminAuth.isLoading,
      userType: 'hr' as const,
      applicant: null,
      hrUser: adminAuth.hrUser,
      sessionToken: adminAuth.sessionToken,
      error: adminAuth.error,
      isSessionExpired: false,
      
      // Use admin methods
      sendMagicLink: async () => ({ success: false, message: 'Not available for HR users' }),
      verifyMagicLink: async () => ({ success: false, message: 'Not available for HR users' }),
      hrLogin: adminAuth.hrLogin,
      logout: adminAuth.logout,
      clearError: adminAuth.clearError,
      refreshSession: adminAuth.refreshSession,
      handleSessionTimeout: () => {},
      clearSessionTimeout: () => {},
    };
  }

  // If candidate is authenticated, use candidate auth
  if (candidateAuth.isAuthenticated) {
    return {
      isAuthenticated: candidateAuth.isAuthenticated,
      isLoading: candidateAuth.isLoading,
      userType: 'applicant' as const,
      applicant: candidateAuth.applicant,
      hrUser: null,
      sessionToken: null,
      error: candidateAuth.error,
      isSessionExpired: candidateAuth.isSessionExpired,
      
      // Use candidate methods
      sendMagicLink: candidateAuth.sendMagicLink,
      verifyMagicLink: candidateAuth.verifyMagicLink,
      hrLogin: async () => ({ success: false, message: 'Not available for applicants' }),
      logout: candidateAuth.logout,
      clearError: candidateAuth.clearError,
      refreshSession: candidateAuth.refreshSession,
      handleSessionTimeout: candidateAuth.handleSessionTimeout,
      clearSessionTimeout: candidateAuth.clearSessionTimeout,
    };
  }

  // Neither is authenticated - decide based on route and loading state
  
  if (isAdminRoute) {
    // On admin routes, prioritize admin auth
    return {
      isAuthenticated: false,
      isLoading: adminAuth.isLoading,
      userType: null,
      applicant: null,
      hrUser: null,
      sessionToken: null,
      error: adminAuth.error,
      isSessionExpired: false,
      
      sendMagicLink: async () => ({ success: false, message: 'Not available for HR users' }),
      verifyMagicLink: async () => ({ success: false, message: 'Not available for HR users' }),
      hrLogin: adminAuth.hrLogin,
      logout: adminAuth.logout,
      clearError: adminAuth.clearError,
      refreshSession: adminAuth.refreshSession,
      handleSessionTimeout: () => {},
      clearSessionTimeout: () => {},
    };
  }

  // On candidate routes or general routes, use candidate auth
  return {
    isAuthenticated: false,
    isLoading: candidateAuth.isLoading,
    userType: null,
    applicant: null,
    hrUser: null,
    sessionToken: null,
    error: candidateAuth.error,
    isSessionExpired: candidateAuth.isSessionExpired,
    
    sendMagicLink: candidateAuth.sendMagicLink,
    verifyMagicLink: candidateAuth.verifyMagicLink,
    hrLogin: async () => ({ success: false, message: 'Not available for applicants' }),
    logout: candidateAuth.logout,
    clearError: candidateAuth.clearError,
    refreshSession: candidateAuth.refreshSession,
    handleSessionTimeout: candidateAuth.handleSessionTimeout,
    clearSessionTimeout: candidateAuth.clearSessionTimeout,
  };
}

// Hook to check if user is authenticated
export function useAuthGuard(): { isAuthenticated: boolean; isLoading: boolean } {
  const { isAuthenticated, isLoading } = useAuth();
  return { isAuthenticated, isLoading };
}

// Company permission utilities
export type CompanyAction = 'create' | 'read' | 'update' | 'delete' | 'logo';

// Permission matrix for company operations
const COMPANY_PERMISSIONS: Record<string, CompanyAction[]> = {
  HR_STAFF: ['read'],
  HR_ADMIN: ['read', 'update', 'logo'],
  GROUP_ADMIN: ['create', 'read', 'update', 'delete', 'logo'],
};

// Hook for company permissions
export function useCompanyPermissions() {
  const { hrUser } = useAuth();

  const hasCompanyPermission = useCallback((action: CompanyAction): boolean => {
    if (!hrUser) return false;
    const permissions = COMPANY_PERMISSIONS[hrUser.role] || [];
    return permissions.includes(action);
  }, [hrUser]);

  const canAccessCompany = useCallback((companyId: string): boolean => {
    if (!hrUser) return false;
    
    // GROUP_ADMIN has access to all companies
    if (hrUser.role === 'GROUP_ADMIN') {
      return true;
    }
    
    // Other roles need company in their scope
    return hrUser.scopeCompanyIds.includes(companyId);
  }, [hrUser]);

  const canCreateCompany = useCallback((): boolean => {
    return hasCompanyPermission('create');
  }, [hasCompanyPermission]);

  const canEditCompany = useCallback((companyId: string): boolean => {
    return hasCompanyPermission('update') && canAccessCompany(companyId);
  }, [hasCompanyPermission, canAccessCompany]);

  const canDeleteCompany = useCallback((): boolean => {
    return hasCompanyPermission('delete');
  }, [hasCompanyPermission]);

  const canUploadLogo = useCallback((companyId: string): boolean => {
    return hasCompanyPermission('logo') && canAccessCompany(companyId);
  }, [hasCompanyPermission, canAccessCompany]);

  const getAccessibleCompanyIds = useCallback((): string[] => {
    if (!hrUser) return [];
    
    // GROUP_ADMIN has access to all companies (return empty array to indicate "all")
    if (hrUser.role === 'GROUP_ADMIN') {
      return [];
    }
    
    // Other roles return their scope
    return hrUser.scopeCompanyIds;
  }, [hrUser]);

  return {
    hasCompanyPermission,
    canAccessCompany,
    canCreateCompany,
    canEditCompany,
    canDeleteCompany,
    canUploadLogo,
    getAccessibleCompanyIds,
    userRole: hrUser?.role,
    scopeCompanyIds: hrUser?.scopeCompanyIds || [],
  };
}
